import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Badge, Button, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { useChatDock } from '@/lib/ChatDockContext';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * The "AI Agents" page (PRD §3.1) — replaces the generic CrudPage for AppAiAgent with a
 * purpose-built list (scope/status/model/spend, a Chat button per row). Embed keys moved
 * to their own nav item (EmbedKeysPage.jsx). Route→agent bindings are a separate,
 * not-yet-built feature (AppAgentRouteBinding was deliberately deferred, TDD §5.7/§5.8).
 */
function AgentStatusBadge({ agent }) {
  if (agent.is_paused) return <Badge variant="warning" size="sm">paused</Badge>;
  if (!agent.is_active) return <Badge variant="secondary" size="sm">inactive</Badge>;
  return <Badge variant="success" size="sm">active</Badge>;
}

/**
 * `company_id` NULL means a platform-wide agent visible to EVERY company; non-null means
 * that one company's own custom agent (models/AppAiAgent.model.js — it deliberately does
 * not extend BaseTenantModel for exactly this reason). Both kinds arrive in the same list
 * with nothing else distinguishing them, so surface it.
 */
function AgentScopeBadge({ agent }) {
  return agent.company_id == null
    ? <Badge variant="primary" size="sm">global</Badge>
    : <Badge variant="secondary" size="sm">company</Badge>;
}

const SCOPE_OPTIONS = [
  { label: 'All scopes', value: '' },
  { label: 'Global only', value: 'global' },
  { label: 'This company', value: 'company' },
];

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
  { label: 'Inactive', value: 'inactive' },
];

function statusOf(a) {
  if (a.is_paused) return 'paused';
  return a.is_active ? 'active' : 'inactive';
}

function spendLabel(a) {
  const spent = a.spend_usd_month != null ? `$${Number(a.spend_usd_month).toFixed(4)}` : '$0';
  return a.monthly_budget_usd != null ? `${spent} / $${Number(a.monthly_budget_usd).toFixed(2)}` : spent;
}

function agentColumns({ nav, openDockWithAgent }) {
  return [
    {
      key: 'name',
      label: 'Name',
      render: (a) => (
        <div>
          <button onClick={() => nav(`/app-ai-agent/${a.id}`)} className="font-medium text-primary-600 hover:underline text-left">
            {a.name}
          </button>
          <div className="text-secondary-500 text-xs max-w-xs truncate" title={a.description}>{a.description}</div>
        </div>
      ),
    },
    { key: 'key', label: 'Key', render: (a) => <span className="font-mono text-xs">{a.key}</span> },
    { key: 'company_id', label: 'Scope', searchable: false, render: (a) => <AgentScopeBadge agent={a} /> },
    { key: 'is_active', label: 'Status', searchable: false, render: (a) => <AgentStatusBadge agent={a} /> },
    { key: 'default_model', label: 'Model', render: (a) => a.default_model || '—' },
    { key: 'spend_usd_month', label: 'Spend / Budget', searchable: false, render: spendLabel },
    {
      key: 'chat',
      label: '',
      sortable: false,
      searchable: false,
      render: (a) => (
        <button
          onClick={() => openDockWithAgent(a.key)}
          className="p-1.5 rounded hover:bg-[var(--app-border)]/30 text-primary-600"
          aria-label={`Chat with ${a.name}`}
        >
          <MessageCircle size={16} />
        </button>
      ),
    },
  ];
}

export default function AgentsPage() {
  const { fetchApi } = useAppApi();
  const { openDockWithAgent } = useChatDock();
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [scope, setScope] = useState('');
  const [status, setStatus] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  // Filtering and paging stay CLIENT-side on purpose: AppAiAgent.afterList post-filters
  // by company AFTER korm has applied limit/offset (korm's where DSL has no verified
  // OR/IS-NULL), so server-side paging over this model returns short pages and an
  // inflated total. Do not "optimise" this into a serverSide DataTable — if >100 agents
  // becomes real, add a bespoke endpoint that does the OR in SQL instead.
  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('crud', { model: 'AppAiAgent', action: 'list', limit: 100 })
      .then((res) => setAgents((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, [reload]);

  const appOptions = useMemo(() => [
    { label: 'All applications', value: '' },
    ...apps.map((a) => ({ label: a.name, value: String(a.id) })),
  ], [apps]);

  const rows = useMemo(() => {
    let filtered = agents.filter((a) => {
      // Loose == mirrors visibleTo() in models/AppAiAgent.model.js: company_id may arrive
      // as null from JSON or be absent entirely.
      if (scope === 'global' && a.company_id != null) return false;
      if (scope === 'company' && a.company_id == null) return false;
      return !status || statusOf(a) === status;
    });
    if (applicationId) filtered = filtered.filter((item) => item.application_id == null || item.application_id === Number(applicationId));
    return filtered;
  }, [agents, scope, status, applicationId]);

  const columns = useMemo(() => agentColumns({ nav, openDockWithAgent }), [nav, openDockWithAgent]);

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="AI Agents"
        subtitle={(
          <>
            {agents.length} agent{agents.length === 1 ? '' : 's'} configured
            {rows.length !== agents.length ? ` · ${rows.length} shown` : ''}.
          </>
        )}
        actions={(
          <Can module="agents" action={writeActionFor('agents')}>
            <Button size="sm" onClick={() => nav('/app-ai-agent/new')}>New agent</Button>
          </Can>
        )}
      />

      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        error={err}
        emptyText="No agents yet."
        search={{ placeholder: 'Search agents…', keys: ['name', 'key', 'description', 'default_model'] }}
        toolbar={(
          <>
            <div className="w-48 sm:w-56"><Select options={appOptions} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable /></div>
            <div className="w-40"><Select options={SCOPE_OPTIONS} value={scope} onChange={setScope} placeholder="All scopes" /></div>
            <div className="w-40"><Select options={STATUS_OPTIONS} value={status} onChange={setStatus} placeholder="All statuses" /></div>
          </>
        )}
        onReload={reload}
      />
    </PageShell>
  );
}
